import 'core-js/actual/array/at'
import '@mdi/font/css/materialdesignicons.css'
import App from './App.vue'
import { createApp } from 'vue'
import { setupRouterMiddleware } from '@/middleware/routeGuard.js'
import { createRouter, createWebHistory } from 'vue-router'
import { createPinia } from 'pinia'
import routes from 'virtual:generated-pages'
import { Amplify } from 'aws-amplify'
import './styles/settings.scss'
import * as Sentry from '@sentry/vue'
import { DateTime } from 'luxon'
import VersionChecker from '@/service/VersionChecker.js'
import { cognitoUserPoolsTokenProvider } from 'aws-amplify/auth/cognito'
import { App as CapacitorApp } from '@capacitor/app'
import { Capacitor } from '@capacitor/core'
import { StatusBar } from '@capacitor/status-bar'
import { Keyboard, KeyboardResize } from '@capacitor/keyboard'
import privateStorage from '@/store/privateStorage.js'
import { createVuetify } from 'vuetify'
import 'vuetify/styles'
import { loadFonts } from '@/plugins/webfontloader'
import { addGoogleAnalyticsToHead, setNativeAppStatusBarColor, userPrefersDarkMode } from '@/service/uiUtils.js'
import pushNotifications from '@/service/pushNotifications.js'
import { initMixpanelClicks } from '@/service/mixpanelClicks.js'
import { SplashScreen } from '@capacitor/splash-screen'
import { CapacitorUpdater } from '@capgo/capacitor-updater'

function initSentry(app) {
    console.log(`Sentry disabled: ${process.env.KN_DISABLE_SENTRY}`)
    Sentry.init({
        enabled: !process.env.KN_DISABLE_SENTRY,
        app,
        dsn: process.env.SENTRY_DSN,
        integrations: [
            // Sentry.browserTracingIntegration({ router }),
            // Sentry.replayIntegration(),
        ],
        environment: process.env.KN_ENV + '-ui',
        // Tracing
        tracesSampleRate: 0.0,  // Set to 0 to disable performance tracing
        // tracesSampleRate: 0.1, //  Capture 10% of the transactions
        // Set 'tracePropagationTargets' to control for which URLs distributed tracing should be enabled
        tracePropagationTargets: ['http://localhost:3000/', process.env.KN_API_ENDPOINT],
        // Session Replay
        // replaysSessionSampleRate: 0.1, // This sets the sample rate at 10%. You may want to change it to 100% while in development and then sample at a lower rate in production.
        // replaysOnErrorSampleRate: 1.0, // If you're not already sampling the entire session, change the sample rate to 100% when sampling sessions where errors occur.
        beforeSend(event, hint) {
            const err = hint.originalException
            if (err?.name === 'AuthRequiredError' && err?.message === 'no token') return null
            const message = err?.message || event.exception?.values?.[0]?.value
            if (/^Unable to preload CSS for \/assets\/.+\.css$/.test(message)) return null
            return event
        },
    })
    Sentry.setTag('build_time', DateTime.utc().toString())
}

function initAmplify() {
    cognitoUserPoolsTokenProvider.setKeyValueStorage(new privateStorage.CapacitorSecureStorage())
    const params = {
        Auth: {
            Cognito: {
                userPoolId: process.env.KN_USER_POOL_ID,
                userPoolClientId: process.env.KN_USER_POOL_CLIENT_ID,
                loginWith: {
                    oauth: {
                        domain: `${process.env.KN_ENV}-kinectin.auth.us-east-1.amazoncognito.com`,
                        scopes: ['openid', 'profile', 'email'],
                        redirectSignIn: [
                            `https://${process.env.KN_ENV}.kinectin.com/auth_callback`,
                            'http://localhost:3000/auth_callback',
                        ],
                        redirectSignOut: [
                            `https://${process.env.KN_ENV}.kinectin.com/signin`,
                            'http://localhost:3000/signin',
                        ],
                        responseType: 'code',
                        providers: ['Google', 'Apple'],
                    },
                },
            },
        },
        API: {
            REST: {
                'kinectin-api': { endpoint: process.env.KN_API_ENDPOINT },
            },
        },
    }
    if (Capacitor.isNativePlatform()) {
        const oauth = params.Auth.Cognito.loginWith.oauth
        oauth.redirectSignIn.push(
            'com.kinectin.app://localhost/auth_callback',
            'https://localhost/auth_callback',
        )
        oauth.redirectSignOut.push(
            'com.kinectin.app://localhost/signin',
            'https://localhost/signin',
        )
    }
    Amplify.configure(params)
}

function initRouter(app) {
    const router = createRouter({
        history: createWebHistory(),
        routes,
        scrollBehavior(to, from, savedPosition) {
            return savedPosition ? savedPosition : { top: 0 }
        },
    })
    setupRouterMiddleware({ router })
    app.use(router)
    return router
}

async function initVuetify(app) {
    const vuetify = createVuetify({
        theme: {
            defaultTheme: await userPrefersDarkMode() ? 'dark' : 'light',
            themes: {
                light: {
                    dark: false,
                    colors: {
                        primary: '#4b43ce',
                        secondary: '#00A383',
                    },
                },
                dark: {
                    dark: true,
                    colors: {
                        primary: '#4464ff',
                        secondary: '#00A383',
                    },
                },
            },
        },
    })
    await loadFonts()
    app.use(vuetify)
}

function initCapacitor(router) {
    if (!Capacitor.isNativePlatform()) return

    CapacitorUpdater.notifyAppReady()
        .then(() => console.log('Capgo: appReady'))
        .catch(err => console.error('Capgo notifyAppReady failed:', err))
    CapacitorUpdater.addListener('downloadFailed', (err) => {
        console.error('Capgo downloadFailed:', err)
    }).catch(err => console.error('Capgo downloadFailed listener failed:', err))
    if (process.env.KN_DISABLE_CAPGO === 'true') {
        CapacitorUpdater.setMultiDelay({
            delayConditions: [{ kind: 'date', value: '2075-01-01T00:00:00.000Z' }],
        })
            .then(() => console.log('Capgo: DISABLED'))
            .catch(err => console.error('Capgo disable failed:', err))
    }

    setNativeAppStatusBarColor()
        .then(() => console.log('status bar color set'))
        .catch(err => console.error('status bar color failed:', err))
    console.log('hiding splash bar, etc...')
    setTimeout(async () => {
        await Promise.allSettled([
            SplashScreen.hide(),
            StatusBar.setOverlaysWebView({ overlay: false }),
        ])
        console.log('hiding splash bar, etc... done')
    }, 2000)

    if (Capacitor.getPlatform() === 'ios') {
        Keyboard.setResizeMode({ mode: KeyboardResize.Native })
            .then(() => console.log('keyboard resize mode set'))
            .catch(err => console.error('keyboard resize mode failed:', err))
    }
    CapacitorApp.addListener('appUrlOpen', (event) => {
        console.log(`kinectin: attempting to open url ${event.url}`)
        const url = new URL(event.url)
        const path = url.pathname + url.search + url.hash
        router.push(path).catch(() => {/* ignore navigation duplicates */
        })
    })
        .then(() => console.log('appUrlOpen listener established'))
        .catch(err => console.error('appUrlOpen listener failed:', err))
}

function initPinia(app) {
    const pinia = createPinia()
    app.use(pinia)
}

async function bootstrap() {
    addGoogleAnalyticsToHead()
    const app = createApp(App)
    initSentry(app)
    initPinia(app)
    await initVuetify(app)
    initAmplify()
    const router = initRouter(app)
    initCapacitor(router)
    app.mount('#app')
    initMixpanelClicks()
    VersionChecker.start()
    pushNotifications.init(router)
}

bootstrap().then(() => console.log('bootstrap done'))
